import { ForwardedRef, forwardRef, useMemo } from "react";
import Select, { SelectInstance } from "react-select";
import CreatableSelect from "react-select/creatable";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import useSelectStyles, { SelectThemingProps } from "utils/useSelectStyles";
import useSimpleValue, {
  SimpleValueProps,
} from "components/Shared/Select/useSimpleValue";

type Option<TValues> = { value: TValues; label: string };

export type SimpleSelectProps<
  TValues extends string | number | symbol,
  IsMulti extends boolean = false,
> = Omit<
  StateManagerProps<Option<TValues>, IsMulti>,
  "options" | "value" | "onChange"
> &
  SimpleValueProps<TValues, IsMulti> & {
    isCreatable?: boolean;
    values: TValues[] | readonly TValues[];
    labelsByValue?: Partial<Record<TValues, string>>;
    themingProps?: SelectThemingProps;
  };

function SimpleSelect<
  TValues extends string | number | symbol,
  IsMulti extends boolean = false,
>(
  {
    isCreatable,
    isMulti,
    styles,
    values,
    labelsByValue,
    onChange,
    value: rawValue,
    themingProps,
    ...rest
  }: SimpleSelectProps<TValues, IsMulti>,
  ref: ForwardedRef<SelectInstance<Option<TValues>, IsMulti>>,
) {
  type ValuesOption = Option<TValues>;
  const styleProps = useSelectStyles(styles, themingProps);

  const options = useMemo(
    () =>
      values.slice(0).reduce<ValuesOption[]>((acc, value) => {
        const label = labelsByValue?.[value] || String(value);
        if (label) acc.push({ value, label });
        return acc;
      }, []),
    [values, labelsByValue],
  );

  const getOptionFromValue = useMemo(() => {
    const optionsByValue = options.reduce((acc, opt) => {
      acc[opt.value] = opt;
      return acc;
    }, {} as Record<TValues, ValuesOption>);

    return (v: TValues) => {
      const option = optionsByValue[v];
      if (option) return option;
      return { value: v, label: v } as ValuesOption;
    };
  }, [options]);

  const { value, handleChange } = useSimpleValue({
    getOptionFromValue,
    getValueFromOption: (option) => option.value,
    isMulti,
    onChange,
    value: rawValue,
  });

  if (isCreatable) {
    return (
      <CreatableSelect<ValuesOption, IsMulti>
        {...styleProps}
        {...{ ref, options, isMulti, value }}
        onChange={handleChange}
        formatCreateLabel={(o) => o}
        {...rest}
      />
    );
  }

  return (
    <Select<ValuesOption, IsMulti>
      {...styleProps}
      {...{ ref, options, isMulti, value }}
      onChange={handleChange}
      {...rest}
    />
  );
}

export default forwardRef(SimpleSelect);
